import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  documentService,
  DOCUMENT_TYPES,
  ALLOWED_DOCUMENT_CONTENT_TYPES,
  MAX_DOCUMENT_SIZE,
} from '@/services/documentService';
import { uploadToS3 } from '@/services/upload.service';

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  clinicId: string;
  onUploadComplete?: () => void;
}

const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  open,
  onOpenChange,
  patientId,
  clinicId,
  onUploadComplete,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'saving' | 'complete' | 'error'
  >('idle');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!ALLOWED_DOCUMENT_CONTENT_TYPES.includes(file.type)) {
        toast.error('Invalid file type. Please upload a PDF or image file.');
        return;
      }

      if (file.size > MAX_DOCUMENT_SIZE) {
        toast.error('File too large. Maximum size is 15MB.');
        return;
      }

      setSelectedFile(file);
      setUploadStatus('idle');

      // Auto-fill title from filename if empty
      if (!title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setTitle(nameWithoutExt);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !title.trim()) return;

    try {
      setUploading(true);
      setUploadStatus('uploading');
      setUploadProgress(0);

      // Step 1: Get presigned URL
      const { uploadUrl, key } = await documentService.getDocumentUploadUrl(
        selectedFile.name,
        selectedFile.type
      );

      // Step 2: Upload file to S3
      await uploadToS3(uploadUrl, selectedFile, (percent) => {
        setUploadProgress(percent);
      });

      setUploadStatus('saving');

      // Step 3: Create FHIRDocumentReference record
      const selectedType = DOCUMENT_TYPES.find((t) => t.code === documentType);

      await documentService.createDocument(clinicId, {
        patientId,
        title: title.trim(),
        typeCode: selectedType?.code,
        typeDisplay: selectedType?.display,
        category: selectedType?.code === 'OTHER' ? 'other' : 'clinical',
        description: description.trim() || undefined,
        contentType: selectedFile.type,
        contentUrl: key,
        contentSize: selectedFile.size,
      });

      toast.success('Document uploaded successfully');
      setUploadStatus('complete');

      // Close the dialog BEFORE triggering the parent's refetch. The parent's
      // loading state swaps the tree to a skeleton that unmounts this dialog;
      // if we close later via setTimeout, the dialog remounts with open=true
      // while the refetch is in flight and looks like it reappears.
      onOpenChange(false);

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error: any) {
      toast.error('Upload failed', {
        description: error.response?.data?.message || error.message || 'Please try again',
      });
      setUploadStatus('error');
    } finally {
      setUploading(false);
    }
  };

  // Reset local form state only AFTER Radix's close animation completes.
  // Resetting synchronously on open=false re-renders the dialog with idle
  // content while it's still animating out, which looks like a reappear.
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => {
      setSelectedFile(null);
      setTitle('');
      setDocumentType('');
      setDescription('');
      setUploadStatus('idle');
      setUploadProgress(0);
    }, 300);
    return () => clearTimeout(t);
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (uploading) return;
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a document to this patient's chart. Supported formats: PDF, JPEG, PNG, WebP, HEIC.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File input */}
          <div className="space-y-2">
            <label
              htmlFor="doc-file"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            >
              {selectedFile ? (
                <div className="flex flex-col items-center">
                  <FileText className="h-8 w-8 text-[#00A79D] mb-2" />
                  <span className="text-sm font-medium truncate max-w-[280px]">
                    {selectedFile.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click to select file</span>
                  <span className="text-xs text-gray-400 mt-1">
                    PDF, JPEG, PNG, WebP, HEIC (max 15MB)
                  </span>
                </div>
              )}
              <input
                id="doc-file"
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                onChange={handleFileSelect}
                disabled={uploading}
              />
            </label>
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <label htmlFor="doc-title" className="text-sm font-medium text-gray-700">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              id="doc-title"
              placeholder="e.g., Lab Results - March 2026"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* Document Type */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Document Type</label>
            <Select value={documentType} onValueChange={setDocumentType} disabled={uploading}>
              <SelectTrigger>
                <SelectValue placeholder="Select type (optional)" />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((type) => (
                  <SelectItem key={type.code} value={type.code}>
                    {type.display}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label htmlFor="doc-description" className="text-sm font-medium text-gray-700">
              Description
            </label>
            <Textarea
              id="doc-description"
              placeholder="Optional notes about this document..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              rows={2}
            />
          </div>

          {/* Status indicator */}
          {uploadStatus !== 'idle' && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50">
              {uploadStatus === 'uploading' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-[#00A79D]" />
                  <span className="text-sm">Uploading file... {uploadProgress}%</span>
                </>
              )}
              {uploadStatus === 'saving' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-[#00A79D]" />
                  <span className="text-sm">Saving document record...</span>
                </>
              )}
              {uploadStatus === 'complete' && (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700">Document uploaded!</span>
                </>
              )}
              {uploadStatus === 'error' && (
                <>
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-700">Upload failed</span>
                </>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={uploading} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || !title.trim() || uploading}
              className="flex-1 bg-[#00A79D] hover:bg-[#008c85]"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentUploadDialog;
